<template>
	<div class="flex flex-col sideNav" :class="{ collapsed: !sidebarPinned }">
		<div class="flex flex-col content-between gap-2" id="navMenu">
			<button
				title="Toggle Menu"
				@click="pinMenu()"
				id="expandMenu"
				class="flex justify-end p-3 text-2xl sidebar-button"
				v-tooltip.right="sidebarPinned ? 'Collapse Menu' : 'Expand Menu'"
			>
				<FontAwesomeIcon
					class="fa-lg hover:backdrop-brightness-90"
					:class="{ hide: !sidebarPinned }"
					:icon="['far', sidebarPinned ? 'xmark' : 'bars']"
				/>
			</button>

			<nav class="flex flex-col flex-grow" id="navigationLinks" role="navigation">
				<button
					@click="navto(item.href)"
					:data-href="item.href"
					v-for="(item, index) in visibleNavigation"
					:key="item.name"
					:class="[
						item.current ? 'brightness-150 sidebar-nav-active' : 'hover:bg-secondary hover:backdrop-brightness-90 sidebar-nav-item',
						'flex items-center p-2',
					]"
					v-show="SessionInfo.hasRightsToScreen(item.toCheckAccess, PageAccess.Read) && checkUserTypeAccess(item.name)"
				>
					<span v-tooltip.right="!sidebarPinned ? item.name : ''">
						<FontAwesomeIcon
							:icon="['far', item.icon]"
							class="flex-shrink-0 mr-2 text-xl"
							aria-hidden="true"
							style="width: 1.25em; padding-left: 0.25rem"
						/>
					</span>

					<span class="text-sm font-medium menuItem" :class="{ hide: !sidebarPinned }">
						{{ item.name }}
					</span>
				</button>
			</nav>

			<div class="flex p-4 border-t menuItem sidebar-user-section" :class="{ hide: !sidebarPinned }">
				<div class="flex flex-col flex-shrink-0 w-full group">
					<div class="flex flex-row justify-between align-middle">
						<a href="#" class="p-1 font-medium sidebar-user-name" @click="callUserProfile()">{{ user.fullName }}</a>
					</div>

					<div class="flex `">
						<a href="#" class="text-sm font-medium p-2 sidebar-logout" @click="logout()">Log Out</a>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue"
import { FeatureFlagClient } from "../../entities/NSwagGenerated"
import * as Session from "../auth/personSessionData"
import { useRouter } from "vue-router"
import { library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { EventBus } from "../eventBus"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import { oktaAuth } from "../auth/oktaAuth"

import {
	faArrowFromLeft,
	faArrowFromRight,
	faBars,
	faXmark,
	faExpand,
	faCircleUser,
	faUsers,
	faListTree,
	faFaceSmile,
	faBriefcase,
	faCircleInfo,
	faChartBar,
	faMap,
	faSunBright,
	faFlask,
	faFire,
	faGear,
	faGears,
	faCircleExclamation,
	faGraduationCap,
	faBookOpen,
	faHexagonEquals,
	faBullhorn,
	faSplit,
	faChartMixed,
	faCircleQuestion,
	faTruck,
	faPalette,
} from "@fortawesome/pro-regular-svg-icons"
import { faP } from "@fortawesome/free-solid-svg-icons"

library.add(
	faBars,
	faXmark,
	faArrowFromLeft,
	faArrowFromRight,
	faExpand,
	faListTree,
	faUsers,
	faCircleUser,
	faFaceSmile,
	faBriefcase,
	faCircleInfo,
	faChartBar,
	faMap,
	faSunBright,
	faFlask,
	faFire,
	faGear,
	faGears,
	faCircleExclamation,
	faGraduationCap,
	faBookOpen,
	faHexagonEquals,
	faBullhorn,
	faSplit,
	faChartMixed,
	faCircleQuestion,
	faTruck,
	faPalette
)

interface NavItem {
	name: string
	icon: string
	href: string
	current: boolean
	order: number
	toCheckAccess: string
	featureFlag?: string
}

const sidebarPinned = ref(false)
const sidebarCollapsed = ref(!sidebarPinned)
const navigation = ref(new Array<NavItem>())
const enabledFlags = ref<string[]>([])

const visibleNavigation = computed(() => navigation.value.filter((item) => !item.featureFlag || enabledFlags.value.includes(item.featureFlag)))
const user = Session.SessionInfo.currentUser()
const router = useRouter()
const environmentURL = import.meta.env.VITE_BASE_URL.toString()

onMounted(async () => {
	navigation.value.push(
		{ name: "Dashboard", icon: "chart-mixed", href: "landingPage", current: false, order: 1, toCheckAccess: "Dashboard" },
		{
			name: "System Configuration",
			icon: "gear",
			href: "systemConfiguration",
			current: false,
			order: 2,
			toCheckAccess: "System Level Configuration",
		},
		{
			name: "Dropdown Administration",
			icon: "list-tree",
			href: "dropdownAdministration",
			current: false,
			order: 2.4,
			toCheckAccess: "Dropdown Administration",
		},
		{
			name: "Material Administration",
			icon: "flask",
			href: "materialAdministration",
			current: false,
			order: 2.5,
			toCheckAccess: "Material Administration",
		},
		// {
		// 	name: "Relatable Units",
		// 	icon: "square-poll-vertical",
		// 	href: "relatableUnitAdmin",
		// 	current: false,
		// 	order: 2.55,
		// 	toCheckAccess: "System Level Configuration",
		// },
		{
			name: "Transport Administration",
			icon: "truck",
			href: "transportAdministration",
			current: false,
			order: 2.6,
			toCheckAccess: "Transport Administration",
		},
		{
			name: "Unit Operation Administration",
			icon: "gears",
			href: "unitOperationAdministration",
			current: false,
			order: 2.65,
			toCheckAccess: "Unit Operation Administration",
		},
		{
			name: "Energy Administration",
			icon: "fire",
			href: "energyAdmin",
			current: false,
			order: 2.7,
			toCheckAccess: "Energy Administration",
		},

		{
			name: "Theme Administration",
			icon: "palette",
			href: "systemConfigurationThemeTemplate",
			current: false,
			order: 2.8,
			toCheckAccess: "Theme Administration",
		},
		{
			name: "Help Administration",
			icon: "circle-question",
			href: "helpAdmin",
			current: false,
			order: 2.9,
			toCheckAccess: "Help Administration",
		},
		{
			name: "Projects",
			icon: "briefcase",
			href: "projectList",
			current: false,
			order: 3,
			toCheckAccess: "Projects",
		},
		{
			name: "Knowledge Base",
			icon: "book-open",
			href: "knowledge",
			current: false,
			order: 4,
			toCheckAccess: "Dashboard",
			featureFlag: "Knowledge Base",
		},
		{
			name: "Material Compare",
			icon: "hexagon-equals",
			href: "materialData",
			current: false,
			order: 3.5,
			toCheckAccess: "Dashboard",
			featureFlag: "Material Compare",
		}
	)
	sidebarPinned.value = localStorage.getItem("sidebarPinned") == "true" ? true : false
	navigation.value.sort((a, b) => a.order - b.order)
	const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())
	const flaggedItems = navigation.value.filter((i) => i.featureFlag)
	const results = await Promise.allSettled(
		flaggedItems.map(async (item) => ({
			featureFlag: item.featureFlag!,
			isEnabled: await featureFlagClient.isEnabled(item.featureFlag!),
		}))
	)
	enabledFlags.value = results
		.filter((result): result is PromiseFulfilledResult<{ featureFlag: string; isEnabled: boolean }> =>
			result.status === "fulfilled" && result.value.isEnabled)
		.map((result) => result.value.featureFlag)
})

function checkAccessToPage(pageName: string) {
	return SessionInfo.hasRightsToScreen(pageName, PageAccess.Read)
}

function checkUserTypeAccess(itemName: string) {
	// For Transport Administration, Help Administration, and Unit Operation Administration, only allow Admin, Client Admin, or ERM Admin users
	if (itemName === "Transport Administration" || itemName === "Help Administration" || itemName === "Unit Operation Administration") {
		const userRole = user.role
		return SessionInfo.currentUserIsAdmin() || userRole.indexOf("Client Admin") !== -1 || userRole.indexOf("ERM Admin") !== -1
	}
	// For all other items, allow normal access
	return true
}

function callUserProfile() {
	if (import.meta.env.VITE_ENVIRONMENT_NAME == "Training") {
		router.push("/app/userProfile")
	} else {
		router.push("/app/landingPage")
	}
}

function pinMenu() {
	sidebarPinned.value = !sidebarPinned.value
	localStorage.setItem("sidebarPinned", sidebarPinned.value.toString())
	//sidebarCollapsed.value = !sidebarPinned.value
}

const logout = async () => {
	try {
		// Clear all stored tokens and session data
		sessionStorage.clear()
		localStorage.removeItem("token")
		localStorage.removeItem("PersonSessionData")

		// Clear Okta's token manager
		oktaAuth.tokenManager.clear()

		// Stop the Okta service to prevent any ongoing operations
		await oktaAuth.stop()

		// Sign out from Okta
		await oktaAuth.signOut({
			postLogoutRedirectUri: environmentURL + "/logout/callback",
			clearTokensBeforeRedirect: true, // Ensure tokens are cleared before redirect
		})
	} catch (error) {
		console.error("Logout failed", error)
		// Even if logout fails, clear local data and redirect
		sessionStorage.clear()
		localStorage.removeItem("token")
		localStorage.removeItem("PersonSessionData")
		window.location.href = "/login"
	}
}

function toggleMenu(visible: boolean) {
	if (!sidebarPinned.value) {
		if (!sidebarCollapsed.value) {
			setTimeout(() => {
				sidebarCollapsed.value = visible
			}, 100)
		} else {
			sidebarCollapsed.value = visible
		}
	}
}
function hasAccessToScreen(screenName: string) {
	var access = user.screenAccess.find((s) => s.screenName == screenName)
	if (access) {
		console.log("Access", access)
		return access.accessType!.indexOf("N") < 0
	} else {
		console.log("screen name not found for " + screenName + ` so couldn''t insert into navbar`)
		return false
	}
}

function navto(href: string) {
	EventBus.emit("hideBreadcrumb", {})
	if (router.currentRoute.value.path === "/app/" + href) {
		//reload page'
		router.go(0)
	} else {
		router.push("/app/" + href)
	}
}
</script>

<style scoped>
.sideNav {
	width: 15rem;
	transition: all 0.5s ease-in-out;
	background: var(--main-sidebar-background-color, var(--p-sidebar-background-color, #1a1a2e));
	color: var(--main-sidebar-text-color, #ffffff);
}
.sideNav.collapsed {
	width: 60px;
}

#navMenu {
	position: fixed;
	transition: all 0.5s ease-in-out;
}
.sideNav.collapsed > #navMenu {
	width: 55px;
}
.sideNav > #navMenu {
	width: 15rem;
}

.menuItem {
	white-space: nowrap;
	opacity: 1;
	visibility: visible;
	transition:
		opacity 0.3s ease-in-out,
		visibility 0.3s ease-in-out;
}
#navigationLinks {
	padding-bottom: 2em;
}

/* Sidebar elements using theme colors */
.sidebar-button {
	color: var(--main-sidebar-text-color, #ffffff);
}

.sidebar-nav-item {
	color: var(--main-sidebar-text-color, #ffffff);
	opacity: 0.8;
}

.sidebar-nav-active {
	color: var(--main-sidebar-text-color, #ffffff);
}

.sidebar-user-section {
	/* Uso un bordo più trasparente per non influire sui colori del testo */
	border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.sidebar-user-name {
	color: var(--main-sidebar-text-color, #ffffff);
	opacity: 0.8; /* Stessa opacità dei pulsanti di navigazione */
}

.sidebar-logout {
	color: var(--main-sidebar-text-color, #ffffff);
	opacity: 0.8; /* Stessa opacità dei pulsanti di navigazione */
}

.sidebar-logout:hover {
	opacity: 1;
}

#navMenu button:focus-visible,
a:focus-visible {
	background-color: var(--main-sidebar-background-color, var(--p-sidebar-background-color, #1a1a2e));
	color: var(--main-sidebar-text-color, #ffffff);
}

.menuItem.hide {
	opacity: 0;
	visibility: hidden;
}
</style>
